import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class MediaInterceptorService {

  private renderer: Renderer2;
  private observer: MutationObserver;
  private cachedImages: Map<string, HTMLImageElement> = new Map<string, HTMLImageElement>();
  private cachedVideos: Map<string, Blob> = new Map<string, Blob>();
  private videoObjectUrls: Map<string, string> = new Map<string, string>();

  // Supported file extensions
  private imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  private videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov'];

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  public setupMediaInterception() {
    try {
      // Setup MutationObserver to dynamically observe changes in the DOM
      this.observer = new MutationObserver(mutationsList => {
        mutationsList.forEach(mutation => {
          // Handle added nodes for images, videos, or elements with background media
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              if (node instanceof HTMLElement) {
                if (node.tagName === 'IMG') {
                  this.handleImage(node as HTMLImageElement);
                } else if (node.tagName === 'VIDEO' || node.tagName === 'SOURCE') {
                  this.handleVideo(node as HTMLVideoElement);
                } else {
                  this.checkBackgroundImage(node as HTMLElement);
                }
              }
            });
          }
          // Handle attribute changes for dynamically updated media
          else if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
            const target = mutation.target as HTMLElement;
            if (mutation.attributeName === 'src') {
              if (target.tagName === 'IMG') {
                this.handleImage(target as HTMLImageElement);
              } else if (target.tagName === 'VIDEO' || target.tagName === 'SOURCE') {
                this.handleVideo(target as HTMLVideoElement);
              }
            } else if (mutation.attributeName === 'style') {
              this.checkBackgroundImage(target);
            }
          }
        });
      });

      // Start observing mutations in the document body and its subtree
      this.connectObserver();
      // Initial setup to observe existing media elements
      this.observeExistingMedia();
    } catch (error) {
      console.error('Error setting up media interception:', error);
    }
  }

  connectObserver() {
    try {
      this.observer.observe(document.body, { 
        childList: true, 
        subtree: true, 
        attributes: true, 
        attributeFilter: ['src', 'style'] 
      });
    } catch (error) {
      console.error('Error reconnecting observer:', error);
    }
  }

  private observeExistingMedia() {
    try {
      // Function to observe existing img tags on page load
      const images = document.querySelectorAll('img');
      images.forEach((img: HTMLImageElement) => {
        this.handleImage(img);
      });

      // Function to observe existing video tags on page load
      const videos = document.querySelectorAll('video');
      videos.forEach((video: HTMLVideoElement) => {
        this.handleVideo(video);
      });

      // Check existing elements for background images
      const elements = document.querySelectorAll('*');
      elements.forEach((element: HTMLElement) => {
        this.checkBackgroundImage(element);
      });
    } catch (error) {
      console.error('Error observing existing media:', error);
    }
  }

  private handleImage(img: HTMLImageElement) {
    const src = img.src;
    if (src && this.isImageFile(src)) {
      if (this.cachedImages.has(src)) {
        const cachedImg = this.cachedImages.get(src);
        this.displayImage(cachedImg, img);
      } else {
        this.cacheAndLoadImage(img).then(() => {
          this.displayImage(img, img);
        }).catch((error) => {
          console.error(`Failed to load image: ${src}`, error);
        });
      }
    }
  }

  private handleVideo(video: HTMLVideoElement) {
    const src = video.src;
    if (src && this.isVideoFile(src)) {
      if (this.cachedVideos.has(src)) {
        this.displayCachedVideo(video, src);
      } else {
        this.cacheAndLoadVideo(video, src).then(() => {
          this.displayCachedVideo(video, src);
        }).catch((error) => {
          console.error(`Failed to load video: ${src}`, error);
        });
      }
    }
  }

  private checkBackgroundImage(element: HTMLElement) {
    const computedStyle = getComputedStyle(element);
    const backgroundImage = computedStyle.backgroundImage;
    if (backgroundImage && this.hasImageInBackground(backgroundImage)) {
      const imageUrl = this.extractImageUrl(backgroundImage);
      if (imageUrl) {
        if (this.cachedImages.has(imageUrl)) {
          const cachedImg = this.cachedImages.get(imageUrl);
          this.displayBackgroundImage(element, cachedImg);
        } else {
          const tempImg = new Image();
          tempImg.onload = () => {
            this.cacheImage(imageUrl, tempImg);
            this.displayBackgroundImage(element, tempImg);
          };
          tempImg.onerror = (error) => {
            console.error(`Failed to load background image: ${imageUrl}`, error);
          };
          tempImg.src = imageUrl;
        }
      }
    }
  }

  // Helper methods to check file types
  private isImageFile(url: string): boolean {
    return this.imageExtensions.some(ext => url.toLowerCase().includes(ext));
  }

  private isVideoFile(url: string): boolean {
    return this.videoExtensions.some(ext => url.toLowerCase().includes(ext));
  }

  private hasImageInBackground(backgroundImage: string): boolean {
    return this.imageExtensions.some(ext => backgroundImage.includes(ext));
  }

  // Video caching methods
  private async cacheAndLoadVideo(video: HTMLVideoElement, src: string): Promise<void> {
    try {
      const videoBlob = await this.fetchVideoAsBlob(src);
      this.cachedVideos.set(src, videoBlob);
      
      // Create object URL for the blob
      const objectUrl = URL.createObjectURL(videoBlob);
      this.videoObjectUrls.set(src, objectUrl);
      
      console.log(`Video cached: ${src}`);
    } catch (error) {
      console.error(`Failed to cache video: ${src}`, error);
      throw error;
    }
  }

  private async fetchVideoAsBlob(url: string): Promise<Blob> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.blob();
    } catch (error) {
      throw new Error(`Failed to fetch video: ${error.message}`);
    }
  }

  private displayCachedVideo(video: HTMLVideoElement, originalSrc: string) {
    const objectUrl = this.videoObjectUrls.get(originalSrc);
    if (objectUrl) {
      this.disconnectObserver();
      video.src = objectUrl;
      this.connectObserver();
    }
  }

  // Existing image caching methods (updated)
  private cacheImage(imageUrl: string, img: HTMLImageElement) {
    this.cachedImages.set(imageUrl, img);
  }

  private extractImageUrl(backgroundImage: string): string | null {
    const match = backgroundImage.match(/url\(['"]?(.*?)['"]?\)/);
    return match ? match[1] : null;
  }

  private async cacheAndLoadImage(img: HTMLImageElement): Promise<void> {
    const src = img.src;
    try {
      await this.loadImage(img);
      this.cacheImage(src, img);
    } catch (error) {
      throw error;
    }
  }

  private loadImage(img: HTMLImageElement): Promise<void> {
    return new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (error) => reject(error);
    });
  }

  private displayImage(cachedImg: HTMLImageElement, originalImg: HTMLImageElement) {
    if (cachedImg && cachedImg.complete) {
      this.disconnectObserver();
      originalImg.src = cachedImg.src;
      this.connectObserver();
    }
  }

  private displayBackgroundImage(element: HTMLElement, img: HTMLImageElement) {
    if (img && img.complete) {
      this.disconnectObserver();
      this.renderer.setStyle(element, 'backgroundImage', `url(${img.src})`);
      this.connectObserver();
    }
  }

  disconnectObserver() {
    try {
      this.observer.disconnect();
    } catch (error) {
      console.error('Error disconnecting observer:', error);
    }
  }

  // Cleanup method to revoke object URLs
  private cleanupVideoUrls() {
    this.videoObjectUrls.forEach((objectUrl) => {
      URL.revokeObjectURL(objectUrl);
    });
    this.videoObjectUrls.clear();
  }

  // Cache management methods
  public clearImageCache() {
    this.cachedImages.clear();
  }

  public clearVideoCache() {
    this.cleanupVideoUrls();
    this.cachedVideos.clear();
  }

  public clearAllCache() {
    this.clearImageCache();
    this.clearVideoCache();
  }

  public getCacheStats() {
    return {
      images: this.cachedImages.size,
      videos: this.cachedVideos.size,
      totalItems: this.cachedImages.size + this.cachedVideos.size
    };
  }

  ngOnDestroy(): void {
    try {
      this.disconnectObserver();
      this.cleanupVideoUrls(); // Important: cleanup object URLs to prevent memory leaks
    } catch (error) {
      console.error('Error cleaning up media interceptor:', error);
    }
  }
}
